import { useEffect, useId, useState, createContext, useContext } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import { useMenu } from "@/comn/features/config/MenuConfig";
import { Icon } from "@/comn/components";
import { Logo } from "../Logo";

const MenuModalContext = createContext<any>(null);

const Toggle = () => {
    const { open, setOpen } = useContext(MenuModalContext);

    return (
        <button className="absolute top-0 right-4 w-[20px] h-[60px]" onClick={() => setOpen((prev: any) => !prev)}>
            {open ? <Icon icon="close" size="lg" /> : <Icon icon="menu" size="lg" />}
        </button>
    );
};

const Item = ({ data, current }: any) => {
    const { menuId, menuNm, children, menuUrlAddr, isLeafYn } = data;

    const mobileGnbContextValue = useContext(MenuModalContext);

    const navigate = useNavigate();
    const id = useId();
    const [open, setOpen] = useState(Object.values(current?._parents || {}).includes(menuId));

    const handleClickItem = () => {
        if (children) {
            setOpen((prev: any) => !prev);
            return;
        }

        if (menuUrlAddr) {
            mobileGnbContextValue.setOpen(false);

            if (process.env.REACT_APP_BASE === menuUrlAddr.slice(0, 5)) {
                navigate(`${menuUrlAddr}`);
            } else {
                const origin = window.location.origin;
                window.location.href = origin + menuUrlAddr;
            }
        }
    };

    return (
        <li>
            <button
                aria-current={current?.menuId === menuId ? "page" : "false"}
                onClick={handleClickItem}
                className={classNames(
                    "group p-2 flex w-full text-lg items-center justify-between rounded hover:bg-gray/10 dark:hover:bg-white/5",
                    open ? "text-black font-semibold dark:text-white" : "text-gray dark:text-lightgray",
                    "aria-[current=page]:text-primary aria-[current=page]:font-semibold dark:aria-[current=page]:text-primary",
                )}
            >
                <div className="flex text-left flex-1">
                    {isLeafYn === "Y" && <span className="mr-2">·</span>}
                    <span className="group-aria-[current=page]:underline">{menuNm}</span>
                </div>
                {Boolean(children) && (
                    <Icon icon="down" size="xs" className={classNames("transition", open && "rotate-180")} />
                )}
            </button>
            {!!children && open && (
                <ul className="pl-2 pb-2">
                    {children.map((child: any) => {
                        return <Item key={id + child.menuId} data={child} current={current} />;
                    })}
                </ul>
            )}
        </li>
    );
};

const Content = () => {
    const { root, current } = useMenu();

    const id = useId();

    return (
        <div className="z-[9999] flex flex-col fixed top-0 left-0 w-screen h-screen overflow-y-auto backdrop-blur bg-background/90">
            <div className="flex px-4 items-center justify-between min-h-[60px]">
                <Logo />
                <Toggle />
            </div>

            <nav className="p-4 flex justify-center flex-1">
                <ul className="flex flex-col gap-16">
                    {root?.children?.map((child: any) => {
                        const { menuId, menuNm, bsopClsfCd, children } = child;
                        return (
                            <li key={id + menuId} className={classNames("flex", "sm:flex-col", "md:items-start")}>
                                <div className="flex items-center gap-2 w-[280px] p-2">
                                    <Icon
                                        icon={bsopClsfCd}
                                        className="fill-darkgray group-aria-[current=page]:fill-primary dark:fill-white"
                                    />
                                    <span className="font-semibold min-w-max text-darkgray text-[15px] dark:text-white group-aria-[current=page]:text-primary">
                                        {menuNm}
                                    </span>
                                </div>
                                {Boolean(children) && (
                                    <ul className="w-[400px] flex flex-col gap-1">
                                        {children.map((child: any) => {
                                            return <Item key={id + child.menuId} data={child} current={current} />;
                                        })}
                                    </ul>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </nav>

            <div className="min-h-[100px]"></div>
        </div>
    );
};

export const HeaderMenuToggle = () => {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (open) document.body.style.overflowY = "hidden";
        else document.body.style.overflowY = "auto";
        return () => {
            document.body.style.overflowY = "auto";
        };
    }, [open]);

    return (
        <MenuModalContext.Provider value={{ open, setOpen }}>
            <Toggle />
            {open && createPortal(<Content />, document.body)}
        </MenuModalContext.Provider>
    );
};
